// Admin/privileged roller icin dashboard iskeleti.
// Tum ortak mantik BaseLayout'ta. Bu dosya admin nav elemanlarini,
// sirket adini ve admin sayfalarina ozgu main-inner modifier'larini saglar.
import React, { useMemo } from "react";
import BaseLayout from "./BaseLayout";
import { useI18n } from "../providers/I18nProvider";
import { useAuth } from "../contexts/AuthContext";
import { EXTERNAL_TOOLS } from "../config";
import {
  canManageOrganization,
  canViewSiteActivities,
  hasRole,
  ROLE,
} from "../utils/roles";

const hasToolPermission = (toolPermissions, key) =>
  Boolean(toolPermissions.find((p) => p.tool_key === key && p.granted));

const getMainInnerModifier = (pathname) => {
  if (pathname === "/admin/my-team") return "main-inner--my-team";
  if (pathname === "/admin/holidays") return "main-inner--admin-holidays";
  if (pathname === "/admin/leaves-me") return "main-inner--admin-leaves-me";
  if (pathname === "/admin/leaves") return "main-inner--admin-leaves";
  if (pathname === "/admin/team-leaves") return "main-inner--admin-team-leaves";
  return "";
};

const AdminDashboardLayout = ({ children }) => {
  const { t } = useI18n();
  const { user: userData, toolPermissions } = useAuth();

  const navSections = useMemo(() => {
    const canManageOrg = canManageOrganization(userData);
    const siteActivitiesVisible = canViewSiteActivities(userData);
    const isAdminRole = hasRole(userData, ROLE.ADMIN);
    const canReviewSat =
      hasRole(userData, ROLE.IT) ||
      hasRole(userData, ROLE.COMPANY_MANAGER) ||
      isAdminRole ||
      hasRole(userData, ROLE.HR) ||
      hasRole(userData, ROLE.UNIT_SUPERVISOR);
    // Başvurular: admin/it/company_manager rolü VEYA 'applications' tool izni
    const canReviewApplications =
      isAdminRole ||
      hasRole(userData, ROLE.IT) ||
      hasRole(userData, ROLE.COMPANY_MANAGER) ||
      hasToolPermission(toolPermissions, "applications");
    // CCTV: backend middleware ile aynı kural (admin/it/company_manager VEYA
    // 'cctv' tool izni).
    const canViewCctv =
      isAdminRole ||
      hasRole(userData, ROLE.IT) ||
      hasRole(userData, ROLE.COMPANY_MANAGER) ||
      hasToolPermission(toolPermissions, "cctv");

    return [
      {
        title: t("nav.general"),
        items: [
          { label: t("nav.home"), path: "/dashboard", match: "exact" },
          {
            label: t("nav.siteActivities"),
            path: "/admin/site-activities",
            match: "exact",
            hidden: !siteActivitiesVisible,
          },
        ],
      },
      {
        title: t("nav.siteSettings"),
        items: [
          {
            label: t("nav.generalSettings"),
            path: "/admin/settings",
            match: "exact",
            hidden: !canManageOrg,
          },
          {
            label: t("nav.integrationKeys"),
            path: "/admin/integration-keys",
            match: "exact",
            hidden: !isAdminRole,
          },
          {
            label: t("nav.mailSettings"),
            path: "/admin/mail-settings",
            match: "exact",
            hidden: !canManageOrg,
          },
        ],
      },
      {
        title: t("nav.leave"),
        items: [
          { label: t("nav.leaves"), path: "/admin/leaves-me", match: "exact" },
          { label: t("nav.leaveTypes"), path: "/admin/leave-types", match: "exact" },
          {
            label: t("nav.publicHolidays"),
            path: "/admin/holidays",
            match: "exact",
            hidden: !canManageOrg,
          },
        ],
      },
      {
        title: t("nav.team"),
        items: [
          { label: t("nav.myTeam"), path: "/admin/my-team", match: "exact" },
          { label: t("nav.teamLeaves"), path: "/admin/team-leaves", match: "exact" },
        ],
      },
      {
        title: t("nav.approvals"),
        items: [
          { label: t("nav.leaveRequestsAdmin"), path: "/admin/leaves", match: "exact" },
          {
            label: t("nav.reservationRequests"),
            path: "/admin/reservations",
            match: "exact",
            hidden: !siteActivitiesVisible,
          },
        ],
      },
      {
        title: t("nav.fleet"),
        items: [
          {
            label: t("nav.vehicleManagement"),
            path: "/admin/vehicles",
            match: "prefix",
            hidden: !isAdminRole,
          },
          {
            label: t("nav.vehicleCalendar"),
            path: "/vehicles",
            match: "prefix",
          },
          {
            label: t("nav.myReservations"),
            path: "/admin/my-reservations",
            match: "exact",
          },
        ],
      },
      {
        title: t("nav.sat"),
        items: [
          { label: t("nav.mySat"), path: "/sat-requests", match: "prefix" },
          {
            label: t("nav.satReview"),
            path: "/admin/sat-requests",
            match: "exact",
            hidden: !canReviewSat,
          },
        ],
      },
      {
        title: t("nav.cctv"),
        items: [
          {
            label: t("nav.cctvLive"),
            path: "/cctv",
            match: "prefix",
            hidden: !canViewCctv,
          },
        ],
      },
      {
        title: t("nav.organization"),
        items: [
          { label: t("nav.users"), path: "/admin/users", match: "exact", hidden: !canManageOrg },
          { label: t("nav.teams"), path: "/admin/teams", match: "exact", hidden: !canManageOrg },
          {
            label: "Araç İzinleri",
            path: "/admin/tool-permissions",
            match: "exact",
            hidden: !canManageOrg,
          },
        ],
      },
      {
        title: t("nav.applications"),
        items: [
          {
            label: t("nav.applicationsList"),
            path: "/admin/applications",
            match: "exact",
            hidden: !canReviewApplications,
          },
          {
            label: t("nav.applicationTypes"),
            path: "/admin/application-types",
            match: "exact",
            hidden: !isAdminRole,
          },
          {
            label: t("nav.applicationTags"),
            path: "/admin/application-tags",
            match: "exact",
            hidden: !(isAdminRole || hasRole(userData, ROLE.IT)),
          },
        ],
      },
      {
        title: "Şirket Araçları",
        items: [
          {
            label: "Tools",
            path: EXTERNAL_TOOLS.tools,
            match: "exact",
            hidden: !hasToolPermission(toolPermissions, "tools"),
            external: true,
          },
          {
            label: "BTO Website",
            path: EXTERNAL_TOOLS.cms,
            match: "exact",
            hidden: !hasToolPermission(toolPermissions, "cms"),
            external: true,
          },
          {
            label: "GitLab Editor",
            path: EXTERNAL_TOOLS.gitlabEditor,
            match: "exact",
            hidden: !hasToolPermission(toolPermissions, "gitlab_editor"),
            external: true,
          },
        ],
      },
    ];
  }, [t, userData, toolPermissions]);

  return (
    <BaseLayout
      navSections={navSections}
      companyName="BOZANKAYA TEKNOLOJİ A.Ş"
      getMainInnerModifier={getMainInnerModifier}
    >
      {children}
    </BaseLayout>
  );
};

export default AdminDashboardLayout;
